import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Check, X, Target, Calendar as CalIcon, RefreshCw, Lock, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { PRIORITY_V3, STATE_V3, INTENT_V3 } from '../../../design-system/taskIdentity';
import { PRIORITY_OPTIONS, EFFORT_OPTIONS } from '../../../lib/taskConstants';

/** Popover — wraps a pill, opens priority picker. */
export function PriorityPopoverPill({ children, task, onSave, testId = 'oth-edit-priority' }) {
  const current = String(task?.task_priority ?? '3');
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="oth-edit-pill-btn" onClick={(e) => e.stopPropagation()} data-testid={testId}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-40 p-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary px-2 pt-1 pb-1 font-semibold">Priority</div>
        <div className="space-y-0.5">
          {PRIORITY_OPTIONS.map((p) => {
            const meta = PRIORITY_V3[p.key];
            const active = current === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => onSave(p.value)}
                className={`w-full flex items-center gap-2 text-xs px-2 py-1 rounded-md transition-colors ${active ? 'bg-violet-100 dark:bg-violet-900/30 font-semibold' : 'hover:bg-accent'}`}
                data-testid={`oth-priority-opt-${p.key}`}
              >
                <span className="w-1 h-4 rounded" style={{ backgroundColor: meta?.color }} />
                <span className="font-mono">{meta?.label}</span>
                <span className="orka-text-tertiary text-[11px] ml-auto">{meta?.long?.replace(meta?.label, '').trim()}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function StatePopoverPill({ children, task, onSave, testId = 'oth-edit-state' }) {
  const current = task?.task_status || 'To Do';
  const options = [
    { key: 'open', legacy: 'To Do' },
    { key: 'inProgress', legacy: 'In Progress' },
    { key: 'partial', legacy: 'Partial' },
    { key: 'blocked', legacy: 'Blocked' },
    { key: 'done', legacy: 'Done' },
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="oth-edit-pill-btn" onClick={(e) => e.stopPropagation()} data-testid={testId}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary px-2 pt-1 pb-1 font-semibold">State</div>
        <div className="space-y-0.5">
          {options.map(({ key, legacy }) => {
            const meta = STATE_V3[key];
            const active = current === legacy;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSave(legacy)}
                className={`w-full flex items-center gap-2 text-xs px-2 py-1 rounded-md transition-colors ${active ? 'bg-violet-100 dark:bg-violet-900/30 font-semibold' : 'hover:bg-accent'}`}
                data-testid={`oth-state-opt-${key}`}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta?.color }} />
                <span>{meta?.label}</span>
                <span className="orka-text-tertiary text-[11px] ml-auto font-mono">{meta?.short}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const INTENT_ICONS = { focus: Target, plan: CalIcon, recover: RefreshCw, private: Lock, system: Sparkles, commit: Check };
export function IntentPopoverPill({ children, task, onSave, testId = 'oth-edit-intent' }) {
  const current = task?.intent || task?.task_intent || 'focus';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="oth-edit-pill-btn" onClick={(e) => e.stopPropagation()} data-testid={testId}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary px-2 pt-1 pb-1 font-semibold">Intent</div>
        <div className="space-y-0.5">
          {Object.values(INTENT_V3).map((meta) => {
            const Icon = INTENT_ICONS[meta.key] || Target;
            const active = current === meta.key;
            return (
              <button
                key={meta.key}
                type="button"
                onClick={() => onSave(meta.key)}
                className={`w-full flex items-center gap-2 text-xs px-2 py-1 rounded-md transition-colors ${active ? 'bg-violet-100 dark:bg-violet-900/30 font-semibold' : 'hover:bg-accent'}`}
                data-testid={`oth-intent-opt-${meta.key}`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <span>{meta.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function EffortPopoverPill({ children, task, onSave, testId = 'oth-edit-effort' }) {
  const current = task?.task_estimated_effort ?? 1;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="oth-edit-pill-btn" onClick={(e) => e.stopPropagation()} data-testid={testId}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary px-2 pt-1 pb-1 font-semibold">Effort</div>
        <div className="space-y-0.5">
          {EFFORT_OPTIONS.map((o) => {
            const active = current === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => onSave(o.value)}
                className={`w-full flex items-center justify-between text-xs px-2 py-1 rounded-md transition-colors ${active ? 'bg-violet-100 dark:bg-violet-900/30 font-semibold' : 'hover:bg-accent'}`}
                data-testid={`oth-effort-opt-${o.value}`}
              >
                <span className="font-mono">E{o.value}</span>
                <span className="orka-text-tertiary text-[11px]">{o.minutes < 60 ? `${o.minutes}m` : `${Math.round(o.minutes / 60)}h`}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TimePopoverPill({ children, task, onSave, testId = 'oth-edit-time' }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(task?.task_start_time || '');
  const [end, setEnd] = useState(task?.task_end_time || '');
  useEffect(() => {
    setStart(task?.task_start_time || '');
    setEnd(task?.task_end_time || '');
  }, [task?.task_start_time, task?.task_end_time]);

  const commit = useCallback(() => {
    const updates = { task_start_time: start || null, task_end_time: end || null };
    if (start && end) {
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      const dur = (eh * 60 + em) - (sh * 60 + sm);
      if (dur > 0) {
        updates.task_estimated_effort = dur <= 15 ? 1 : dur <= 30 ? 2 : dur <= 60 ? 3 : dur <= 180 ? 5 : dur <= 360 ? 8 : 13;
      }
    }
    onSave(updates);
    setOpen(false);
  }, [start, end, onSave]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="oth-edit-pill-btn" onClick={(e) => e.stopPropagation()} data-testid={testId}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary mb-2 font-semibold">Scheduled time</div>
        <div className="flex items-center gap-2 mb-2">
          <label className="flex-1">
            <div className="text-[10px] orka-text-tertiary mb-0.5">Start</div>
            <input
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full text-xs px-2 py-1 border rounded orka-border-subtle orka-surface-raised"
              data-testid="oth-time-start"
            />
          </label>
          <label className="flex-1">
            <div className="text-[10px] orka-text-tertiary mb-0.5">End</div>
            <input
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full text-xs px-2 py-1 border rounded orka-border-subtle orka-surface-raised"
              data-testid="oth-time-end"
            />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="text-[11px] px-2 py-1 rounded hover:bg-accent" onClick={() => setOpen(false)} data-testid="oth-time-cancel">Cancel</button>
          <button type="button" className="text-[11px] px-2 py-1 rounded bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-medium" onClick={commit} data-testid="oth-time-save">Save</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TitlePopoverPill({ children, task, onSave, testId = 'oth-edit-title' }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(task?.task_title || task?.title || '');
  useEffect(() => { setVal(task?.task_title || task?.title || ''); }, [task?.task_title, task?.title]);
  const inputRef = useRef(null);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.select(), 10); }, [open]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="oth-edit-title-btn" onClick={(e) => e.stopPropagation()} data-testid={testId}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary mb-1 font-semibold">Title</div>
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { onSave(val); setOpen(false); }
              if (e.key === 'Escape') setOpen(false);
            }}
            className="flex-1 text-xs px-2 py-1.5 border rounded orka-border-subtle orka-surface-raised"
            data-testid="oth-title-input"
          />
          <button className="p-1 rounded hover:bg-green-100 text-green-600" onClick={() => { onSave(val); setOpen(false); }} data-testid="oth-title-save"><Check className="w-3.5 h-3.5" /></button>
          <button className="p-1 rounded hover:bg-red-100 text-red-600" onClick={() => setOpen(false)}><X className="w-3.5 h-3.5" /></button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function WorkAreaPopoverPill({ children, task, options = [], onSave, testId = 'oth-edit-work-area' }) {
  const current = task?.work_area_id || 'General';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="oth-edit-pill-btn" onClick={(e) => e.stopPropagation()} data-testid={testId}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary px-2 pt-1 pb-1 font-semibold">Work area</div>
        <div className="space-y-0.5 max-h-56 overflow-auto">
          {options.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => onSave(a)}
              className={`w-full text-left text-xs px-2 py-1 rounded-md transition-colors ${current === a ? 'bg-violet-100 dark:bg-violet-900/30 font-semibold' : 'hover:bg-accent'}`}
              data-testid={`oth-work-area-opt-${a}`}
            >
              {a}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
